import { distort } from './utils/distortion'; // Import distortion utility function
import { composables } from '@/composables'
const soundData = composables().soundDataStore;

class Particle {
  constructor({ x, y, radius = 1, width = 1920, height = 1080 }) {
    this.x = distort(x, y, width, height).x;
    this.y = distort(x, y, width, height).y;
    this.width = width;
    this.height = height;
    this.ax = 0;
    this.ay = 0;
    this.vx = 0;
    this.vy = 0;
    this.ix = x;
    this.iy = y;
    this.radius = radius;
    this.baseRadius = radius; // Store the initial base radius
    this.maxScale = 3; // Maximum scaling factor
    this.minDist = width * 0.02; // Minimum distance for push force
    this.pushFactor = 0.25; // this.pushFactor = 0.5; // Push force multiplier
    this.pullFactor = 0.5; // this.pullFactor = 0.1; // Pull force multiplier
    this.dampFactor = 0.5; // this.dampFactor = 0.8; // Damping factor for velocity
    this.cursorDistance = 0;

    // Cache frequently used values for performance
    this.centerX = width * 0.5;
    this.centerY = height * 0.5;
    this.maxDistSq = (width * 0.5) ** 2 + (height * 0.5) ** 2;
    this.minDistSq = this.minDist * this.minDist;
  }

  // Apply the pull force towards the initial position
  applyPullForce(blend, time, soundSpectrum) {
    const dx = distort(this.ix, this.iy, this.width, this.height, time, blend, soundSpectrum).x - this.x;
    const dy = distort(this.ix, this.iy, this.width, this.height, time, blend, soundSpectrum).y - this.y;
    this.ax = dx * this.pullFactor;
    this.ay = dy * this.pullFactor;
  }

  // Apply the push force based on cursor position - optimized
  applyPushForce(cursor) {
    const dx = this.x - cursor.x.value;
    const dy = this.y - cursor.y.value;
    const distSq = dx * dx + dy * dy; // Use squared distance to avoid sqrt

    if (distSq < this.minDistSq && distSq > 0) {
      this.cursorDistance = Math.sqrt(distSq); // Only sqrt when necessary
      const distDelta = this.minDist - this.cursorDistance;
      const invDist = 1 / this.cursorDistance;
      this.ax += dx * invDist * distDelta * this.pushFactor;
      this.ay += dy * invDist * distDelta * this.pushFactor;
    } else {
      this.cursorDistance = distSq < this.minDistSq ? Math.sqrt(distSq) : this.minDist;
    }
  }

  // Update particle's velocity
  updateVelocity() {
    this.vx += this.ax;
    this.vy += this.ay;
    this.vx *= this.dampFactor;
    this.vy *= this.dampFactor;
  }

  updateDistort(blend, time) {
    this.x = distort(this.ix, this.iy, this.width, this.height, 0, blend, time).x;
    this.y = distort(this.ix, this.iy, this.width, this.height, 0, blend, time).y;
  }

  updateHover() {
    this.x = this.x + this.vx;
    this.y = this.y + this.vy;
  }

  // Calculate edge scale based on distance from center - optimized
  calculateEdgeScale() {
    const dx = this.x - this.centerX;
    const dy = this.y - this.centerY;
    const distSqFromCenter = dx * dx + dy * dy;

    // Normalize squared distance (avoids one sqrt)
    const normalizedDistSq = distSqFromCenter / this.maxDistSq;

    // Scale stays small, then grows aggressively near edges (quartic curve)
    const minScale = 0.005;
    const maxScale = 3.0;
    const normalized = Math.sqrt(normalizedDistSq); // One sqrt instead of two
    const curve = normalized * normalized * normalized * normalized; // ^4
    return minScale + curve * (maxScale - minScale);
  }

  // Adjust the radius based on velocity and edge distance
  adjustRadius() {
    const edgeScale = this.calculateEdgeScale();
    const velocityMag = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
    const velocityScale = Math.min(2.0, 1 + velocityMag * 0.1);

    // Apply edge scaling first, then velocity on top, with minimum size cap
    this.radius = Math.max(0.95, this.baseRadius * edgeScale * velocityScale);
  }

  // Update the particle's position and radius
  update(cursor, blend, time, soundSpectrum) {
    //this.updateDistort(blend, time);
    this.applyPullForce(blend, time, soundSpectrum);
    this.applyPushForce(cursor);
    this.updateVelocity();
    this.updateHover();
    this.adjustRadius();
  }

  // Draw the particle on the canvas - optimized (removed save/restore overhead)
  draw(context) {
    context.beginPath();
    context.fillStyle = 'rgb(200, 200, 200)';
    context.arc(this.x, this.y, this.radius, 0, 6.283185307179586); // 2*PI as constant
    context.fill();
  }

  // New method to update and draw the particle
  updateAndDraw(cursor, context, blend, time = 1) {
    const soundSpectrum = soundData.value?.length ? soundData.value : [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0];
    this.update(cursor, blend, time, soundSpectrum);
    this.draw(context);
  }
}

export default Particle;
